
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import api from '@/api';
import { RootActions } from '@/store/types';
import { ResourcesActions } from '@/store/modules/resources/types';
import { ProductsActions } from '@/store/modules/products/types';
import { Resource } from '@/store/modules/resources/resource';
import ResourceFile from '@/store/modules/resources/file';
import Country from '@/store/models/country';
import Image, { ImageJSON } from '@/store/models/image';
import NotificationBanner from '@/components/blocks/notification-banner.vue';
import FormInput from '@/components/controls/form-input.vue';
import ProductSelectionControl from '@/components/controls/product-selection.vue';
import TreatmentSelectionControl from '@/components/controls/treatment-selection.vue';
import CountriesSelection from '@/components/controls/countries-selection.vue';
import LanguagePicker from '@/components/controls/language-picker.vue';
import ResourceCategoriesForm from '@/components/forms/resource-categories.vue';
import { PublishStatus } from '@/store/types';
import Treatment from '@/store/modules/treatments/treatment';
import Product from '@/store/modules/products/product';
import { CategoryActions } from '@/store/modules/categories/types';
import Taxonomy from '@/store/models/taxonomy';
import createSlug from '@/utils/slug';

interface UploadedFileData {
  id: string;
  file: File;
  uploadedFile?: any; // The response from the upload API
  /** Stable URL for row thumbnail (CDN/imgix from API, or blob only as fallback for images). */
  resolvedThumbnailUrl?: string;
  resourceName: string;
  resourceDescription: string;
  productId?: number;
  treatmentId?: number;
  countries: Country[];
  categories: Taxonomy[];
  languageCode: string;
  distributorResource: boolean;
  isSaving: boolean;
  isSaved: boolean;
  savedResourceId?: number;
  savedResourceTitle?: string;
}

export default Vue.extend({
  name: 'BulkUploadResources',
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    NotificationBanner,
    FormInput,
    ProductSelectionControl,
    TreatmentSelectionControl,
    CountriesSelection,
    LanguagePicker,
    ResourceCategoriesForm,
  },
  data() {
    return {
      uploadedFiles: [] as UploadedFileData[],
      isDragging: false,
      isUploading: false,
      uploadProgress: 0,
      uploadedCount: 0,
      totalFiles: 0,
      isSavingAll: false,
    };
  },
  computed: {
    ...mapState('resources', {
      editableResource: 'editable',
    }),
    canSaveAll(): boolean {
      const hasUnsaved = this.uploadedFiles.some((f) => !f.isSaved);
      return (
        this.uploadedFiles.length > 0 &&
        hasUnsaved &&
        this.uploadedFiles.every((fileData, index) =>
          fileData.isSaved || this.isRowValid(index),
        ) &&
        !this.isSavingAll
      );
    },
    /** Matches resources SAVE_EDITABLE clinical branch (`bulk-upload-clinical-support-resources`). */
    isClinicalSupportBulk(): boolean {
      return this.$route.name === 'bulk-upload-clinical-support-resources';
    },
  },
  created() {
    this.$store.dispatch(RootActions.LOAD_COUNTRIES, { force: false });
    this.$store.dispatch('products/' + ProductsActions.LOAD_TREE);
    this.$store.dispatch('categories/' + CategoryActions.LOAD_ALL);
  },
  methods: {
    ...mapActions('resources', {
      saveResource: ResourcesActions.SAVE_EDITABLE,
      updateResource: ResourcesActions.UPDATE_EDITABLE,
      loadEmptyEditable: ResourcesActions.LOAD_EMPTY_EDITABLE,
    }),
    triggerFileSelect() {
      (this.$refs.fileInput as HTMLInputElement)?.click();
    },
    handleFileSelect(event: Event) {
      const input = event.target as HTMLInputElement;
      if (input.files && input.files.length > 0) {
        this.processFiles(Array.from(input.files));
      }
    },
    handleDrop(event: DragEvent) {
      this.isDragging = false;
      event.preventDefault();
      if (event.dataTransfer?.files) {
        this.processFiles(Array.from(event.dataTransfer.files));
      }
    },
    processFiles(files: File[]) {
      // Filter to only allow supported file types
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ];
      const allowedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
      
      const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      
      const validFiles = files.filter((file) => {
        const isImage = imageTypes.includes(file.type);
        const isAllowedType = allowedTypes.includes(file.type);
        const hasAllowedExtension = allowedExtensions.some((ext) =>
          file.name.toLowerCase().endsWith(ext),
        );
        return isImage || isAllowedType || hasAllowedExtension;
      });

      if (validFiles.length === 0) {
        this.$store.dispatch(RootActions.ERROR, new Error(this.$t('No valid files selected.')));
        return;
      }

      // Initialize file data
      this.uploadedFiles = validFiles.map((file) => ({
        id: `file_${Date.now()}_${Math.random()}`,
        file,
        uploadedFile: undefined,
        resolvedThumbnailUrl: undefined,
        resourceName: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
        resourceDescription: '',
        productId: undefined,
        treatmentId: undefined,
        countries: [],
        categories: [],
        languageCode: '',
        distributorResource: false,
        isSaving: false,
        isSaved: false,
      }));

      this.uploadFiles();
    },
    async uploadFiles() {
      this.isUploading = true;
      this.uploadProgress = 0;
      this.uploadedCount = 0;
      this.totalFiles = this.uploadedFiles.length;

      try {
        for (let i = 0; i < this.uploadedFiles.length; i++) {
          const fileData = this.uploadedFiles[i];
          const formData = new FormData();
          formData.append('file', fileData.file, fileData.file.name);

          try {
            const response = await api.upload.file(formData, {
              onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                  const fileProgress = (progressEvent.loaded / progressEvent.total) * 100;
                  const overallProgress =
                    ((i + progressEvent.loaded / progressEvent.total) / this.totalFiles) * 100;
                  this.uploadProgress = overallProgress;
                }
              },
            });

            const data = response.data;
            this.$set(this.uploadedFiles[i], 'uploadedFile', data);

            if (this.isImageFile(fileData.file)) {
              const remote = [data.thumbnail, data.preview, data.uri, data.s3].find(
                (x: unknown) => typeof x === 'string' && String(x).trim().length > 0,
              );
              let resolved = remote ? String(remote).trim() : '';
              if (!resolved) {
                resolved = URL.createObjectURL(fileData.file);
              }
              this.$set(this.uploadedFiles[i], 'resolvedThumbnailUrl', resolved);
            } else {
              this.$set(this.uploadedFiles[i], 'resolvedThumbnailUrl', undefined);
            }
            this.uploadedCount++;
          } catch (error) {
            console.error(`Failed to upload ${fileData.file.name}:`, error);
            this.$store.dispatch(
              RootActions.ERROR,
              new Error(this.$t('Failed to upload: %(filename)', { filename: fileData.file.name })),
            );
          }
        }
      } finally {
        this.isUploading = false;
        this.uploadProgress = 100;
      }
    },
    updateFileData(index: number, updates: Partial<UploadedFileData>) {
      this.$set(this.uploadedFiles, index, { ...this.uploadedFiles[index], ...updates });
    },
    onBulkProductChange(index: number, productId: number | undefined) {
      const prev = this.uploadedFiles[index].productId;
      const updates: Partial<UploadedFileData> = { productId };
      if (prev !== productId) {
        updates.treatmentId = undefined;
      }
      this.updateFileData(index, updates);
    },
    onDistributorResourceChange(index: number, event: Event) {
      const checked = (event.target as HTMLInputElement).checked;
      this.updateFileData(index, { distributorResource: checked });
    },
    isRowValid(index: number): boolean {
      const fileData = this.uploadedFiles[index];
      return (
        Boolean(fileData.resourceName && fileData.resourceName.trim()) &&
        Boolean(fileData.languageCode) &&
        fileData.countries.length > 0
      );
    },
    hasRowErrors(index: number): boolean {
      if (this.uploadedFiles[index]?.isSaved) {
        return false;
      }
      const fieldNames = [
        `resource_name_${index}`,
        `language_${index}`,
        `countries_${index}`,
      ];
      return fieldNames.some((fieldName) => this.errors.has(fieldName));
    },
    isSavingRow(index: number): boolean {
      return this.uploadedFiles[index]?.isSaving || false;
    },
    async saveRow(index: number) {
      const fileData = this.uploadedFiles[index];
      if (!this.isRowValid(index) || !fileData.uploadedFile) {
        return;
      }

      this.$set(this.uploadedFiles[index], 'isSaving', true);

      try {
        await this.loadEmptyEditable();
        
        // Get the current editable resource from store
        const resource = this.editableResource || new Resource();
        
        // Set basic fields
        resource.title = fileData.resourceName;
        resource.description = fileData.resourceDescription || '';
        resource.languageCode = fileData.languageCode;
        resource.countries = fileData.countries;
        resource.distributor_resource = fileData.distributorResource ? 1 : 0;
        resource.status = PublishStatus.Published;

        // Set categories
        if (fileData.categories && fileData.categories.length) {
          resource.categories = [...fileData.categories];
        }

        // Set products
        if (fileData.productId) {
          const productResponse = await api.products.get(fileData.productId);
          const product = Product.fromJSON(productResponse.data);
          resource.products = [product];
        }

        // Set treatments 
        if (fileData.treatmentId) {
          const treatmentResponse = await api.treatments.get(fileData.treatmentId);
          const treatment = Treatment.fromJSON(treatmentResponse.data);
          resource.treatments = [treatment];
        }

        // Upload response matches ImageJSON; map to ResourceFile so the asset is downloadable (PDF, Office, etc.).
        const uploadedFile = fileData.uploadedFile as ImageJSON & Record<string, unknown>;
        const assetId = Number(uploadedFile.id);
        const resourceFile = new ResourceFile();
        resourceFile.id = assetId;
        resourceFile.fid = assetId;
        resourceFile.url = String(uploadedFile.uri || uploadedFile.s3 || '');
        resourceFile.title = String(uploadedFile.title || fileData.resourceName || fileData.file.name);
        resourceFile.name = String(uploadedFile.name || fileData.file.name);
        resourceFile.mime = String(uploadedFile.mime || fileData.file.type || '');
        resourceFile.uri = String(uploadedFile.uri || '');
        resourceFile.thumbnail = uploadedFile.thumbnail != null ? String(uploadedFile.thumbnail) : '';
        resourceFile.preview =
          typeof uploadedFile.preview === 'string' ? uploadedFile.preview : String(uploadedFile.preview || '');
        resourceFile.weight = 0;

        resource.files = [resourceFile];

        // Images: also set resource thumbnail (listing/preview). Documents: files only.
        if (this.isImageFile(fileData.file)) {
          const thumb = new Image();
          thumb.fromJSON(uploadedFile as Partial<ImageJSON>);
          thumb.title = String(uploadedFile.title || fileData.resourceName || fileData.file.name);
          resource.thumbnail = thumb;
        } else {
          resource.thumbnail = undefined;
        }

        // Update the editable resource
        this.updateResource(resource);

        // Save the resource
        await this.saveResource();

        const saved = this.editableResource;
        if (saved?.id) {
          this.$set(this.uploadedFiles[index], 'savedResourceId', saved.id);
          this.$set(this.uploadedFiles[index], 'savedResourceTitle', saved.title);
        }
        this.$set(this.uploadedFiles[index], 'isSaved', true);
        this.$store.dispatch('notifications/SHOW_SUCCESS', {
          body: this.$t('Resource saved successfully: %(name)', { name: fileData.resourceName }),
        });
      } catch (error) {
        console.error('Failed to save resource:', error);
        this.$store.dispatch(RootActions.ERROR, error);
      } finally {
        this.$set(this.uploadedFiles[index], 'isSaving', false);
      }
    },
    discardRow(index: number) {
      this.uploadedFiles.splice(index, 1);
    },
    async saveAll() {
      if (!this.canSaveAll) {
        return;
      }

      this.isSavingAll = true;

      try {
        for (let i = 0; i < this.uploadedFiles.length; i++) {
          if (!this.uploadedFiles[i].isSaved) {
            await this.saveRow(i);
          }
        }

        this.$store.dispatch('notifications/SHOW_SUCCESS', {
          body: this.$t('All resources saved successfully.'),
        });
      } catch (error) {
        console.error('Failed to save all resources:', error);
        this.$store.dispatch(RootActions.ERROR, error);
      } finally {
        this.isSavingAll = false;
      }
    },
    isImageFile(file: File): boolean {
      if (file.type && file.type.startsWith('image/')) {
        return true;
      }
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
    },
    shouldShowImageThumbnail(fileData: UploadedFileData): boolean {
      return this.isImageFile(fileData.file) && Boolean(fileData.resolvedThumbnailUrl);
    },
    getThumbnailPlaceholderIcon(fileData: UploadedFileData): string {
      if (this.isImageFile(fileData.file) && !fileData.resolvedThumbnailUrl) {
        return 'images';
      }
      return this.getFileIcon(fileData.file.name);
    },
    getFileIcon(fileName: string): string {
      const ext = fileName.split('.').pop()?.toLowerCase();
      if (ext === 'pdf') return 'document';
      if (['doc', 'docx'].includes(ext || '')) return 'document';
      if (['xls', 'xlsx'].includes(ext || '')) return 'document';
      if (['ppt', 'pptx'].includes(ext || '')) return 'document';
      return 'document';
    },
    formatFileSize(bytes: number): string {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    getEditHref(fileData: UploadedFileData): string {
      if (!fileData.savedResourceId) {
        return '#';
      }
      return this.$router.resolve({
        name: this.isClinicalSupportBulk ? 'clinical-support-edit-resource' : 'edit-resource',
        params: {
          slug: createSlug(fileData.savedResourceTitle || fileData.resourceName || 'resource'),
          id: String(fileData.savedResourceId),
        },
      }).href;
    },
  },
});
